import React, { useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

/**
 * DS Dialog — POS 리뉴얼 모달 셸 (figma 2608:137824 미등록 가맹점 알림 / 2373:84864 영수증 설정하기).
 *
 * DSModal(70px 헤더)과 다른 신형 패턴:
 *  - 헤더 82h: padding left 20 / right 16 / y 20, 닫기 X 42×42(아이콘 22)
 *  - 푸터 82h slot: padding x24 y20, 우측 정렬 gap 12
 *  - 프레임: radius 12(기본)|16, shadow 2px 6px 24px rgba(1,1,1,0.12)
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose - X/ESC 닫기 (backdrop 클릭으로는 닫지 않음 — DSModal과 동일 정책)
 * @param {string} props.title
 * @param {string} [props.subtitle] - 타이틀 아래 보조 텍스트 (figma hidden 변형)
 * @param {number} [props.width=520]
 * @param {"lg"|"md"} [props.titleSize="lg"] - lg=Heading/2 22 (알림형), md=SemiBold 18 ls-2% (영수증 설정하기형)
 * @param {boolean} [props.headerDivider=false] - 헤더 하단 1px Border/Default
 * @param {boolean} [props.footerDivider=false] - 푸터 상단 1px Border/Default
 * @param {boolean} [props.paper=false] - 프레임 bg BG/Paper (본문 배경이 paper인 대형 모달)
 * @param {16|12} [props.radius=12]
 * @param {React.ReactNode} [props.footer] - 우측 정렬 버튼 그룹 slot
 * @param {React.ReactNode} props.children - body
 */
const DSDialog = ({
  open,
  onClose,
  title,
  subtitle,
  width = 520,
  titleSize = "lg",
  headerDivider = false,
  footerDivider = false,
  paper = false,
  radius = 12,
  footer,
  children,
}) => {
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === "Escape") onClose && onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="ds-modal-backdrop">
      <div
        className={[
          "ds-dialog",
          paper && "ds-dialog--paper",
          radius === 16 && "ds-dialog--radius-16",
        ]
          .filter(Boolean)
          .join(" ")}
        style={{ width }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div
          className={`ds-dialog__header${
            headerDivider ? " ds-dialog__header--divider" : ""
          }`}
        >
          <div className="ds-dialog__title-wrap">
            <span
              className={`ds-dialog__title ds-dialog__title--${titleSize}`}
            >
              {title}
            </span>
            {subtitle ? (
              <span className="ds-dialog__subtitle">{subtitle}</span>
            ) : null}
          </div>
          <button
            type="button"
            className="ds-dialog__close"
            onClick={onClose}
            aria-label="닫기"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
        <div className="ds-dialog__body">{children}</div>
        {footer ? (
          <div
            className={`ds-dialog__footer${
              footerDivider ? " ds-dialog__footer--divider" : ""
            }`}
          >
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default React.memo(DSDialog);
